/**
 * @file src/hooks/users.ts
 * @exports canCreateOrganizationForRole
 * @exports canDeleteOrganizationForRole
 * @exports canEditVisibleOrganizationForRole
 * @exports getHighestRoleAssignment
 * @exports useUpdateSelectedOrganizationId
 * @exports useUserOrgRole
 * @summary User role helpers and hooks for selected-organization and organization-level permissions.
 */
import { useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { useMeQuery, useUpdateOneUserMutation } from "src/generated/graphql";
import { getEffectiveRoleAssignment } from "src/helpers/userPermissions";
import { currentOrgIdAtom } from "src/shame/shame-atoms";
import { UserRoleEnum } from "src/types";

// Associate a number with each role to indicate the level of access
// The higher the number, the more privileged the role
// This is useful for comparing access levels between roles, so we can determine
// the highest and lowest privilege levels a user has across multiple orgs
const ROLE_ACCESS_LEVEL: Record<string, number> = {
	[UserRoleEnum.User]: 0,
	[UserRoleEnum.Processor]: 0,
	[UserRoleEnum.IntegrationAdmin]: 1,
	[UserRoleEnum.OrganizationAdmin]: 2,
	[UserRoleEnum.CraAdmin]: 3,
	[UserRoleEnum.CerebrumTeam]: 4,
	[UserRoleEnum.SuperAdmin]: 5,
};

type RoleAssignmentWithOrganization = {
	organization?: { id?: string | null } | null;
	organizationId?: string | null;
	roleV2: {
		name: string;
	};
};

export const getHighestRoleAssignment = ({
	roleAssignments,
}: {
	roleAssignments?: RoleAssignmentWithOrganization[] | null;
}) => {
	if (!roleAssignments?.length) {
		return null;
	}

	return roleAssignments.reduce<string>((highestAccessRole, roleAssignment) => {
		const roleName = roleAssignment.roleV2.name;
		const roleAccessLevel = ROLE_ACCESS_LEVEL[roleName];

		if (roleAccessLevel === undefined) {
			return highestAccessRole;
		}

		const highestAccessLevel = ROLE_ACCESS_LEVEL[highestAccessRole];

		return roleAccessLevel > highestAccessLevel ? roleName : highestAccessRole;
	}, UserRoleEnum.User);
};

export const canCreateOrganizationForRole = ({ role }: { role?: string | null }) =>
	Boolean(
		role &&
			[
				UserRoleEnum.CraAdmin,
				UserRoleEnum.IntegrationAdmin,
				UserRoleEnum.OrganizationAdmin,
				UserRoleEnum.SuperAdmin,
			].includes(role as UserRoleEnum),
	);

export const canDeleteOrganizationForRole = ({ role }: { role?: string | null }) => role === UserRoleEnum.SuperAdmin;

export const canEditVisibleOrganizationForRole = ({ role }: { role?: string | null }) =>
	Boolean(
		role &&
			[
				UserRoleEnum.CraAdmin,
				UserRoleEnum.IntegrationAdmin,
				UserRoleEnum.OrganizationAdmin,
				UserRoleEnum.SuperAdmin,
			].includes(role as UserRoleEnum),
	);

export const useUserOrgRole = () => {
	const { data: userData } = useMeQuery();
	const user = userData?.me;
	const orgId = useAtomValue(currentOrgIdAtom);

	if (!user) {
		return null;
	}

	// If orgId is null, we want to find the highest access role across all orgs
	// the user belongs to
	if (orgId === null) {
		return getHighestRoleAssignment({ roleAssignments: user.roleAssignment }) ?? UserRoleEnum.User;
	}

	const role = getEffectiveRoleAssignment({
		organizationId: orgId,
		roleAssignments: user.roleAssignment,
	});

	return role?.roleV2?.name ?? null;
};

export const useUpdateSelectedOrganizationId = () => {
	const queryClient = useQueryClient();

	const { mutateAsync: updateOneUser } = useUpdateOneUserMutation();

	return ({ id, selectedOrganizationId }: { id: string; selectedOrganizationId: string | null }) =>
		updateOneUser(
			{
				where: {
					id,
				},
				data: {
					selectedOrganizationId: { set: selectedOrganizationId },
				},
			},
			{
				onSuccess: () => {
					queryClient.invalidateQueries({ queryKey: ["me"] });
				},
			},
		);
};
